import React, { useEffect, useRef, useState } from "react"
import { Button, Checkbox, Col, Input, InputNumber, Modal, Row, Select, Steps, message } from "antd"
import { gen, swimminglyApi } from "./utils"

const { Option } = Select

type CourseType = "SCY" | "SCM" | "LCM" | "CUSTOM" | null

const UNIT_YARDS = 0
const UNIT_METERS = 1
const UNIT_FEET = 2

export interface ICreatedMeetPool {
  poolId: number
}

interface ICreateMeetPoolModalProps {
  visible: boolean
  targetClubId: number
  clubName: string
  onCancel: () => void
  onCreated: (pool: ICreatedMeetPool) => void
}

export default function CreateMeetPoolModal({
  visible,
  targetClubId,
  clubName,
  onCancel,
  onCreated,
}: ICreateMeetPoolModalProps): JSX.Element {
  const [saving, setSaving] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [courseType, setCourseType] = useState<CourseType>(null)
  const [customLength, setCustomLength] = useState<number | null>(null)
  const [customUnit, setCustomUnit] = useState<number | null>(null)
  const [customCourseLabel, setCustomCourseLabel] = useState("CUSTOM")
  const [lanes, setLanes] = useState<number | null>(null)
  const [address, setAddress] = useState("")
  const [lat, setLat] = useState<number | null>(null)
  const [lng, setLng] = useState<number | null>(null)
  const [primary, setPrimary] = useState(false)
  const [addressSelected, setAddressSelected] = useState(false)

  const autocompleteContainerRef = useRef<HTMLDivElement | null>(null)
  const placeAutocompleteRef = useRef<any>(null)

  const resetForm = () => {
    setCurrentStep(0)
    setName("")
    setDescription("")
    setCourseType(null)
    setCustomLength(null)
    setCustomUnit(null)
    setCustomCourseLabel("CUSTOM")
    setLanes(null)
    setAddress("")
    setLat(null)
    setLng(null)
    setPrimary(false)
    setAddressSelected(false)
  }

  useEffect(() => {
    if (!visible) {
      resetForm()
      if (autocompleteContainerRef.current) {
        autocompleteContainerRef.current.innerHTML = ""
      }
      placeAutocompleteRef.current = null
      return
    }

    if (currentStep !== 3) {
      if (autocompleteContainerRef.current) {
        autocompleteContainerRef.current.innerHTML = ""
      }
      placeAutocompleteRef.current = null
      return
    }

    const google = (window as any).google
    const container = autocompleteContainerRef.current

    if (!container || !google?.maps?.places) {
      return
    }

    if (!placeAutocompleteRef.current) {
      try {
        const autocompleteElement = new google.maps.places.PlaceAutocompleteElement({
          includedPrimaryTypes: ["geocode"],
        })

        autocompleteElement.addEventListener("gmp-select", async (event: any) => {
          try {
            const placeObj = event.placePrediction?.toPlace
              ? event.placePrediction.toPlace()
              : null

            if (!placeObj?.fetchFields) return

            await placeObj.fetchFields({
              fields: ["formattedAddress", "location"],
            })

            const formattedAddress = placeObj.formattedAddress || ""
            const location = placeObj.location

            if (!location || typeof location.lat !== "function" || typeof location.lng !== "function") {
              return
            }

            setAddress(formattedAddress)
            setLat(location.lat())
            setLng(location.lng())
            setAddressSelected(true)
          } catch (err) {
            console.log("CreateMeetPoolModal gmp-select handler error", err)
          }
        })

        container.innerHTML = ""
        container.appendChild(autocompleteElement)
        placeAutocompleteRef.current = autocompleteElement
      } catch (err) {
        console.log("CreateMeetPoolModal: error initializing PlaceAutocompleteElement", err)
      }
    }
  }, [currentStep, visible])

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((previous) => previous - 1)
    }
  }

  const handleNext = () => {
    if (currentStep === 0) {
      if (!name.trim() || !description.trim()) {
        message.error("Please enter a pool name and description")
        return
      }

      setCurrentStep(1)
      return
    }

    if (currentStep === 1) {
      if (!courseType) {
        message.error("Please select a course")
        return
      }

      if (courseType === "CUSTOM") {
        if (customLength === null || Number.isNaN(customLength)) {
          message.error("Please enter a valid pool length")
          return
        }

        if (customUnit === null) {
          message.error("Please select a unit of measurement")
          return
        }

        if (!customCourseLabel.trim()) {
          message.error("Please select a course label")
          return
        }
      }

      setCurrentStep(2)
      return
    }

    if (!lanes || lanes <= 0) {
      message.error("Please enter the number of lanes")
      return
    }

    setCurrentStep(3)
  }

  const deriveCourseAndLength = () => {
    let course_label = "CUSTOM"
    let pool_length = 0
    let unit = UNIT_YARDS

    if (courseType === "SCY") {
      course_label = "SCY"
      pool_length = 25
      unit = UNIT_YARDS
    } else if (courseType === "SCM") {
      course_label = "SCM"
      pool_length = 25
      unit = UNIT_METERS
    } else if (courseType === "LCM") {
      course_label = "LCM"
      pool_length = 50
      unit = UNIT_METERS
    } else if (courseType === "CUSTOM") {
      course_label = customCourseLabel || "CUSTOM"
      pool_length = customLength === null ? 0 : customLength
      unit = customUnit === null ? UNIT_YARDS : customUnit
    }

    return { course_label, pool_length, unit }
  }

  const handleFinish = async () => {
    const { course_label, pool_length, unit } = deriveCourseAndLength()

    if (!course_label || !pool_length || pool_length <= 0) {
      message.error("Please provide a valid course and pool length")
      return
    }

    const trimmedAddress = address.trim()
    if (!addressSelected || !trimmedAddress || lat === null || lng === null) {
      message.error("Please select an address from the suggestions")
      return
    }

    setSaving(true)
    try {
      const data = await swimminglyApi
        .post(gen("/api/createPoolForActingClub"))
        .body({
          targetClubId,
          pool_name: name.trim(),
          description: description.trim(),
          address: trimmedAddress,
          lat,
          lng,
          pool_length,
          course_label,
          unit_of_measure: unit,
          numLanes: lanes,
          primaryPool: primary,
        })

      if (data.status === "success" && data.pool?.poolId) {
        message.success("Created pool")
        onCreated({ poolId: data.pool.poolId })
        return
      }

      message.error(data.message || "Unable to create pool")
    } catch (err) {
      console.log(err)
      message.error("Unable to create pool")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      destroyOnHidden={true}
      title={`Add Pool For ${clubName}`}
      width={900}
      styles={{
        body: {
          minHeight: currentStep === 3 ? 420 : 320,
          overflow: "visible",
          paddingBottom: currentStep === 3 ? 96 : undefined,
        },
      }}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>,
        ...(currentStep > 0
          ? [
              <Button key="back" onClick={handleBack} disabled={saving}>
                Back
              </Button>,
            ]
          : []),
        currentStep < 3 ? (
          <Button key="next" type="primary" onClick={handleNext} disabled={saving}>
            Next
          </Button>
        ) : (
          <Button key="finish" type="primary" onClick={handleFinish} loading={saving}>
            Create Pool
          </Button>
        ),
      ]}
    >
      <div style={{ marginBottom: 16, color: "rgba(0,0,0,0.65)" }}>
        This pool will be created for {clubName} and can be selected immediately for this meet.
      </div>

      <Steps
        current={currentStep}
        style={{ marginBottom: 24 }}
        items={[
          { title: "Pool Details" },
          { title: "Course" },
          { title: "Lanes" },
          { title: "Address" },
        ]}
      />

      {currentStep === 0 ? (
        <div>
          <Row style={{ marginBottom: 16 }}>
            <Col span={8}>Pool Name</Col>
            <Col span={16}>
              <Input
                placeholder="Main Competition Pool"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </Col>
          </Row>
          <Row>
            <Col span={8}>Pool Description</Col>
            <Col span={16}>
              <Input
                placeholder="8 lane outdoor pool"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </Col>
          </Row>
        </div>
      ) : null}

      {currentStep === 1 ? (
        <div>
          <Row style={{ marginBottom: 16 }}>
            <Col span={8}>Course Type</Col>
            <Col span={16}>
              <Select
                style={{ width: "100%" }}
                value={courseType || undefined}
                onChange={(value) => setCourseType(value as CourseType)}
                placeholder="Select course"
              >
                <Option value="SCY">Short Course Yards</Option>
                <Option value="SCM">Short Course Meters</Option>
                <Option value="LCM">Long Course Meters</Option>
                <Option value="CUSTOM">Custom</Option>
              </Select>
            </Col>
          </Row>
          {courseType === "CUSTOM" ? (
            <div>
              <Row style={{ marginBottom: 16 }}>
                <Col span={8}>Pool Length</Col>
                <Col span={16}>
                  <InputNumber
                    style={{ width: "100%" }}
                    min={1}
                    value={customLength ?? undefined}
                    onChange={(value) =>
                      setCustomLength(typeof value === "number" ? value : null)
                    }
                  />
                </Col>
              </Row>
              <Row style={{ marginBottom: 16 }}>
                <Col span={8}>Units</Col>
                <Col span={16}>
                  <Select
                    style={{ width: "100%" }}
                    value={customUnit ?? undefined}
                    onChange={(value) => setCustomUnit(value)}
                    placeholder="Select units"
                  >
                    <Option value={UNIT_YARDS}>Yards</Option>
                    <Option value={UNIT_METERS}>Meters</Option>
                    <Option value={UNIT_FEET}>Feet</Option>
                  </Select>
                </Col>
              </Row>
              <Row>
                <Col span={8}>Course Label</Col>
                <Col span={16}>
                  <Select
                    style={{ width: "100%" }}
                    value={customCourseLabel}
                    onChange={(value) => setCustomCourseLabel(value)}
                  >
                    <Option value="SCY">SCY</Option>
                    <Option value="SCM">SCM</Option>
                    <Option value="LCM">LCM</Option>
                    <Option value="CUSTOM">Custom</Option>
                  </Select>
                </Col>
              </Row>
            </div>
          ) : null}
        </div>
      ) : null}

      {currentStep === 2 ? (
        <div>
          <Row style={{ marginBottom: 16 }}>
            <Col span={8}>Number of Lanes</Col>
            <Col span={16}>
              <Select
                style={{ width: "100%" }}
                placeholder="6 lanes"
                value={lanes ?? undefined}
                onChange={(value: number) => setLanes(value)}
              >
                <Option value={1}>1 lane</Option>
                <Option value={2}>2 lanes</Option>
                <Option value={3}>3 lanes</Option>
                <Option value={4}>4 lanes</Option>
                <Option value={5}>5 lanes</Option>
                <Option value={6}>6 lanes</Option>
                <Option value={7}>7 lanes</Option>
                <Option value={8}>8 lanes</Option>
                <Option value={9}>9 lanes</Option>
                <Option value={10}>10 lanes</Option>
              </Select>
            </Col>
          </Row>
          <Row style={{ marginBottom: 16 }}>
            <Col span={24}>
              This is the actual number of lanes in the pool. You can change this number whenever you create a meet.
            </Col>
          </Row>
          <Checkbox checked={primary} onChange={(event) => setPrimary(event.target.checked)}>
            Set as primary competition pool for this club
          </Checkbox>
        </div>
      ) : null}

      {currentStep === 3 ? (
        <div>
          <Row style={{ marginBottom: 12 }}>
            <Col span={8}>Pool Address</Col>
            <Col span={16}>
              <div
                ref={autocompleteContainerRef}
                style={{ minHeight: 220, position: "relative", zIndex: 1 }}
              />
            </Col>
          </Row>
          <div style={{ color: "rgba(0,0,0,0.65)", fontSize: 12 }}>
            Choose an address from the suggestions so the meet can use this pool location immediately.
          </div>
        </div>
      ) : null}
    </Modal>
  )
}
